import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Phone, Gauge, Fuel, Cog, Calendar, Palette, Zap, FileDown, Search,
  ShieldCheck, FileCheck, Share2, Link2, Mail, QrCode,
} from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { COMPANY, FEATURES, waLink, fmtPrice, fmtKm, vehicleWaText } from "@/lib/company";
import { WaIcon, useSetWhatsAppText } from "@/lib/whatsapp";
import { usePageMeta } from "@/lib/seo";
import Gallery from "@/components/VehicleGallery";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const has = (x) => x !== null && x !== undefined && x !== "" && x !== 0;

const useVehicleJsonLd = (v) => {
  useEffect(() => {
    if (!v) return;
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "vehicle-jsonld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "Car", name: v.title,
      brand: { "@type": "Brand", name: v.brand }, model: v.model,
      vehicleModelDate: v.year ? String(v.year) : undefined,
      mileageFromOdometer: { "@type": "QuantitativeValue", value: v.mileage, unitCode: "KMT" },
      fuelType: v.fuel, vehicleTransmission: v.transmission, color: v.color, image: v.images?.[0],
      offers: {
        "@type": "Offer", price: v.price, priceCurrency: "EUR",
        availability: v.available ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
        seller: { "@type": "AutoDealer", name: COMPANY.legalName },
      },
    });
    document.head.appendChild(ld);
    return () => document.getElementById("vehicle-jsonld")?.remove();
  }, [v]);
};

const SpecGrid = ({ v }) => {
  const specs = [
    { icon: Calendar, label: "Erstzulassung", value: v.first_registration || v.year },
    { icon: Gauge, label: "Kilometerstand", value: fmtKm(v.mileage) },
    { icon: Zap, label: "Leistung", value: v.power_ps ? `${v.power_ps} PS (${v.power_kw} kW)` : "" },
    { icon: Fuel, label: "Kraftstoff", value: v.fuel },
    { icon: Cog, label: "Getriebe", value: v.transmission },
    { icon: Palette, label: "Farbe", value: v.color },
  ].filter((s) => has(s.value));
  return (
    <div className="grid grid-cols-2 gap-px bg-line rounded-xl overflow-hidden border border-line mt-7" data-testid="vehicle-spec-grid">
      {specs.map(({ icon: Icon, label, value }) => (
        <div key={label} className="bg-surface px-4 py-3.5 flex items-start gap-3">
          <Icon size={16} strokeWidth={1.7} className="mt-0.5 text-brand shrink-0" />
          <div className="min-w-0">
            <p className="text-[10px] text-faint uppercase tracking-wider">{label}</p>
            <p className="text-sm font-semibold text-ink mt-0.5 leading-snug">{value}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

const TechData = ({ v }) => {
  const rows = [
    ["Marke", v.brand], ["Modell", v.model], ["Variante", v.variant], ["Karosserie", v.body_type],
    ["Zustand", v.condition], ["Vorbesitzer", v.owners], ["Motor", v.engine], ["Hubraum", v.displacement],
    ["Leistung", v.power_ps ? `${v.power_ps} PS (${v.power_kw} kW)` : ""],
    ["Türen / Sitze", v.doors && v.seats ? `${v.doors} / ${v.seats}` : ""],
    ["Verbrauch (komb.)", v.consumption], ["CO₂-Emissionen", v.co2], ["FIN", v.vin],
  ].filter(([, val]) => has(val));
  if (!rows.length) return null;
  return (
    <section className="mt-14">
      <h2 className="font-display font-bold text-xl md:text-2xl text-ink mb-6">Technische Daten</h2>
      <div className="grid sm:grid-cols-2 gap-x-10 border-t border-line" data-testid="tech-data-grid">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-4 py-3.5 text-sm border-b border-line">
            <span className="text-faint">{label}</span>
            <span className="font-semibold text-ink text-right">{value}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

const Details = ({ v }) => (
  <>
    {v.highlights?.length > 0 && (
      <div className="flex flex-wrap gap-2 mt-8" data-testid="highlight-badges">
        {v.highlights.map((h) => (
          <span key={h} className="px-3.5 py-1.5 rounded-full bg-surface border border-line text-xs font-medium text-ink">{h}</span>
        ))}
      </div>
    )}
    {v.description && (
      <section className="mt-14">
        <h2 className="font-display font-bold text-xl md:text-2xl text-ink mb-5">Beschreibung</h2>
        <p className="text-dim leading-relaxed whitespace-pre-line" data-testid="vehicle-description">{v.description}</p>
      </section>
    )}
    <TechData v={v} />
    {v.features?.length > 0 && (
      <section className="mt-14">
        <h2 className="font-display font-bold text-xl md:text-2xl text-ink mb-6">Ausstattung</h2>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3" data-testid="vehicle-features">
          {v.features.map((feat) => (
            <p key={feat} className="text-sm text-dim flex items-start gap-3">
              <span className="mt-[8px] w-1.5 h-1.5 rounded-full bg-brand shrink-0" /> {feat}
            </p>
          ))}
        </div>
      </section>
    )}
    {(v.service_history || v.warranty) && (
      <div className="mt-14 grid sm:grid-cols-2 gap-4">
        {[
          v.service_history && { icon: FileCheck, label: "Servicehistorie", value: "Vorhanden" },
          v.warranty && { icon: ShieldCheck, label: "Garantie", value: v.warranty },
        ].filter(Boolean).map(({ icon: Icon, label, value }) => (
          <div key={label} className="rounded-xl border border-line bg-surface p-6">
            <Icon size={19} strokeWidth={1.6} className="mb-3 text-brand" />
            <p className="text-[10px] text-faint uppercase tracking-widest">{label}</p>
            <p className="text-sm font-semibold text-ink mt-1.5">{value}</p>
          </div>
        ))}
      </div>
    )}
  </>
);

const ShareBar = ({ v, onQr }) => {
  const url = `${window.location.origin}${window.location.pathname}`;
  const btn = "flex items-center justify-center w-10 h-10 rounded-full border border-line text-dim hover:text-ink hover:border-silver transition-colors";
  const shareText = `Schau dir dieses Fahrzeug bei ${COMPANY.name} an: ${v.title} – ${fmtPrice(v.price)}\n${url}`;
  return (
    <div className="mt-7 pt-6 border-t border-line" data-testid="share-bar">
    <a data-testid="share-whatsapp-btn" target="_blank" rel="noopener noreferrer"
      href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
      className="btn-pill w-full flex items-center justify-center gap-2.5 border border-[#25D366]/60 bg-[#25D366]/10 text-ink font-semibold py-3.5 text-sm hover:bg-[#25D366]/20 transition-colors">
      <WaIcon size={17} className="text-[#128C7E]" /> Fahrzeug per WhatsApp teilen
    </a>
    <div className="flex items-center gap-2 mt-4">
      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-faint font-semibold mr-auto"><Share2 size={13} /> Weitere Optionen</span>
      <button data-testid="share-copy-btn" aria-label="Link kopieren" className={btn}
        onClick={() => navigator.clipboard?.writeText(url).then(() => toast.success("Link kopiert"))}>
        <Link2 size={15} />
      </button>
      <a data-testid="share-email-btn" aria-label="Per E-Mail teilen" className={btn}
        href={`mailto:?subject=${encodeURIComponent(`${v.title} bei ${COMPANY.name}`)}&body=${encodeURIComponent(`${v.title} – ${fmtPrice(v.price)}\n\n${url}`)}`}>
        <Mail size={15} />
      </a>
      <button data-testid="share-qr-btn" aria-label="QR-Code anzeigen" onClick={onQr} className={btn}><QrCode size={15} /></button>
    </div>
    </div>
  );
};

const Sidebar = ({ v, waText, onQr }) => (
  <div className="lg:sticky lg:top-28 self-start lg:col-start-3 lg:row-start-1 lg:row-span-2 rounded-2xl border border-line bg-surface p-6 md:p-7 card-soft" data-testid="vehicle-sidebar">
    <p className="text-[11px] uppercase tracking-[0.3em] text-brand font-semibold">{v.brand}</p>
    <h1 className="font-display font-bold text-2xl md:text-3xl leading-tight text-ink mt-2" data-testid="vehicle-title">{v.title}</h1>
    <div className="mt-5 flex items-baseline gap-3 flex-wrap">
      <span className="font-display font-bold text-3xl text-ink" data-testid="vehicle-price">{fmtPrice(v.price)}</span>
      {v.vat_deductible && <span className="text-xs text-faint" data-testid="vehicle-vat-note">MwSt. ausweisbar</span>}
    </div>
    <SpecGrid v={v} />
    {v.available ? (
      <div className="space-y-3 mt-7">
        <a href={waLink(waText)} target="_blank" rel="noopener noreferrer" data-testid="vehicle-whatsapp-btn"
          className="btn-pill w-full flex items-center justify-center gap-2.5 bg-[#25D366] text-[#06210f] font-bold uppercase tracking-wider py-4 text-sm hover:brightness-110">
          <WaIcon size={18} /> Per WhatsApp anfragen
        </a>
        <div className="grid grid-cols-1 gap-3">
          <a href={COMPANY.phoneLink} data-testid="vehicle-call-btn"
            className="btn-pill flex items-center justify-center gap-2 border border-line text-ink font-semibold py-3.5 text-sm hover:border-silver transition-colors">
            <Phone size={15} /> Anrufen · {COMPANY.phone}
          </a>
        </div>
        <a href={`${API}/vehicles/${v.id}/expose?base=${encodeURIComponent(window.location.origin)}`} target="_blank" rel="noopener noreferrer"
          data-testid="vehicle-expose-btn" className="flex items-center justify-center gap-2 text-xs uppercase tracking-wider text-dim hover:text-ink py-2 transition-colors">
          <FileDown size={14} /> Exposé als PDF
        </a>
      </div>
    ) : (
      <div className="rounded-xl border border-line bg-elevated/60 p-6 text-center mt-7" data-testid="vehicle-unavailable-note">
        <p className="font-display font-bold text-ink">Dieses Fahrzeug ist nicht mehr verfügbar.</p>
        <p className="text-sm text-dim mt-1.5">Gerne beraten wir Sie zu vergleichbaren Fahrzeugen.</p>
        <Link to={FEATURES.findet ? "/fahrzeugsuche" : "/kontakt"} data-testid="unavailable-findet-btn"
          className="btn-pill inline-flex items-center gap-2 bg-action text-action-ink hover:bg-action-hover font-semibold px-6 py-3 text-sm mt-4">
          {FEATURES.findet ? <><Search size={14} /> Wunschfahrzeug anfragen</> : "Kontakt aufnehmen"}
        </Link>
      </div>
    )}
    <ShareBar v={v} onQr={onQr} />
  </div>
);

const MobileBar = ({ waText, available }) =>
  available ? (
    <div className="lg:hidden fixed bottom-0 inset-x-0 z-50 glass border-t border-line px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] grid grid-cols-[1fr_auto] gap-2" data-testid="vehicle-mobile-bar">
      <a href={waLink(waText)} target="_blank" rel="noopener noreferrer" data-testid="vehicle-mobile-whatsapp-btn"
        className="btn-pill flex items-center justify-center gap-2 bg-[#25D366] text-[#06210f] font-bold text-sm py-3.5">
        <WaIcon size={17} /> Per WhatsApp anfragen
      </a>
      <a href={COMPANY.phoneLink} data-testid="vehicle-mobile-call-btn" aria-label="Anrufen"
        className="btn-pill flex items-center justify-center w-12 border border-line text-ink"><Phone size={17} /></a>
    </div>
  ) : null;

const QrDialog = ({ v, open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="qr-dialog" className="sm:max-w-xs text-center">
      <DialogHeader>
        <DialogTitle className="font-display text-lg text-center">Fahrzeug teilen</DialogTitle>
        <DialogDescription className="text-center">QR-Code scannen und dieses Fahrzeug direkt öffnen</DialogDescription>
      </DialogHeader>
      <img src={`${API}/vehicles/${v.id}/qr?base=${encodeURIComponent(window.location.origin)}`}
        alt="QR-Code" className="w-52 h-52 mx-auto rounded-lg bg-white p-2" data-testid="qr-image" />
    </DialogContent>
  </Dialog>
);

export default function VehicleDetail() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  useEffect(() => {
    setV(null);
    setNotFound(false);
    api.get(`/vehicles/${id}`).then(({ data }) => setV(data)).catch(() => setNotFound(true));
  }, [id]);

  const waText = v ? vehicleWaText(v) : null;
  useSetWhatsAppText(waText);
  usePageMeta(v ? `${v.title} – ${fmtPrice(v.price)}` : null,
    v ? `${v.title}: ${[v.first_registration && `EZ ${v.first_registration}`, fmtKm(v.mileage), v.power_ps && `${v.power_ps} PS`, v.fuel].filter(Boolean).join(", ")} – jetzt bei ${COMPANY.name} in ${COMPANY.region} anfragen.` : null);
  useVehicleJsonLd(v);

  if (notFound)
    return (
      <div className="pt-40 pb-24 text-center px-5" data-testid="vehicle-not-found">
        <h1 className="font-display font-bold text-3xl text-ink mb-4">Fahrzeug nicht gefunden</h1>
        <Link to="/fahrzeuge" className="text-sm text-brand hover:underline">Zurück zum Bestand</Link>
      </div>
    );
  if (!v)
    return (
      <div className="min-h-[60vh] flex items-center justify-center pt-20">
        <div className="w-8 h-8 border-2 border-line border-t-brand rounded-full animate-spin" />
      </div>
    );

  return (
    <div data-testid="vehicle-detail-page" className="pt-20 md:pt-28 pb-32 lg:pb-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Link to="/fahrzeuge" data-testid="back-to-vehicles-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-dim hover:text-brand transition-colors mb-6">
          <ArrowLeft size={14} /> Alle Fahrzeuge
        </Link>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-10 gap-y-8 lg:gap-y-0">
          <div className="lg:col-span-2 min-w-0">
            <Gallery vehicle={v} />
          </div>
          <Sidebar v={v} waText={waText} onQr={() => setQrOpen(true)} />
          <div className="lg:col-span-2 min-w-0">
            <Details v={v} />
          </div>
        </div>
      </div>
      <MobileBar waText={waText} available={v.available} />
      <QrDialog v={v} open={qrOpen} onOpenChange={setQrOpen} />
    </div>
  );
}
